/**
 * Optional template segments — TS twin of
 * `backend/app/services/template_segments.py` (SA-301 grammar, SA-308 guard).
 *
 * `[[ … ]]` in a `templates.*` string is emitted by the engine only when every
 * `$slots.` ref inside resolves non-empty, and dropped otherwise. Non-nesting;
 * single `[` / `]` are literal. Both twins are pinned by
 * `shared/template-segment-cases.json`. Pure module — no React, no store.
 */

export type SegmentFaultKind = 'unclosed' | 'stray_close' | 'nested'

/** `index` is the offset of the offending `[[` (unclosed, nested) or `]]` (stray_close). */
export interface SegmentFault {
  kind: SegmentFaultKind
  index: number
}

export interface SegmentPart {
  kind: 'text' | 'segment'
  text: string
}

const SEGMENT_RE = /\[\[(.*?)\]\]/gs

/**
 * Scan left-to-right over the two-character tokens `[[` and `]]`.
 * depth 0: `[[` opens, `]]` is a stray close. depth 1: `[[` is nested (depth
 * stays 1), `]]` closes. End of text at depth 1 is unclosed, reported at its open.
 */
export function findSegmentFaults(text: string): SegmentFault[] {
  const faults: SegmentFault[] = []
  let openAt: number | null = null
  let i = 0
  while (i < text.length) {
    const pair = text.slice(i, i + 2)
    if (pair === '[[') {
      if (openAt === null) openAt = i
      else faults.push({ kind: 'nested', index: i })
      i += 2
      continue
    }
    if (pair === ']]') {
      if (openAt === null) faults.push({ kind: 'stray_close', index: i })
      else openAt = null
      i += 2
      continue
    }
    i += 1
  }
  if (openAt !== null) faults.push({ kind: 'unclosed', index: openAt })
  return faults
}

/**
 * Clean text → ordered text/segment parts (segment parts carry the body
 * WITHOUT brackets). Faulty text → null: the caller renders it literally and
 * the server's validate finding owns the complaint.
 */
export function splitSegments(text: string): SegmentPart[] | null {
  if (findSegmentFaults(text).length > 0) return null
  const parts: SegmentPart[] = []
  let last = 0
  for (const m of text.matchAll(SEGMENT_RE)) {
    const at = m.index ?? 0
    if (at > last) parts.push({ kind: 'text', text: text.slice(last, at) })
    parts.push({ kind: 'segment', text: m[1] })
    last = at + m[0].length
  }
  if (last < text.length) parts.push({ kind: 'text', text: text.slice(last) })
  return parts
}

/** The editor-hint trigger: the operator has started (or kept) a segment. */
export function hasSegmentSyntax(text: string): boolean {
  return text.includes('[[')
}
